import React, { useEffect, useRef, useState } from "react";
import {
    Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    LinearProgress, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import { getSubjectsAdminByClass, getAuthorsAdminBySubject } from "../../api/subjects";
import { prepareModuleCopy, runModuleCopy } from "../../api/copyModules";

const message = (error) => {
    const data = error?.response?.data;
    return (typeof data === "string" ? data : data?.message) || error?.message || "Не вдалося виконати запит.";
};

export default function CopyModulesDialog({ classId, subjectSlug, authorSlug, onClose, onCopied }) {
    const [sourceClass, setSourceClass] = useState(String(classId));
    const [subject, setSubject] = useState("");
    const [author, setAuthor] = useState("");
    const [subjects, setSubjects] = useState([]);
    const [authors, setAuthors] = useState([]);
    const [plan, setPlan] = useState(null);
    const [completed, setCompleted] = useState(0);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const running = useRef(false);

    useEffect(() => {
        let active = true;
        setLoading(true); setError(""); setPlan(null); setCompleted(0);
        const request = author
            ? prepareModuleCopy([Number(sourceClass), subject, author], [classId, subjectSlug, authorSlug])
            : subject ? getAuthorsAdminBySubject(sourceClass, subject) : getSubjectsAdminByClass(sourceClass);
        request.then((data) => {
            if (!active) return;
            if (author) setPlan(data);
            else if (subject) setAuthors(data.filter((item) => !(sourceClass === String(classId) &&
                subject === subjectSlug && item.authorSlug === authorSlug)));
            else setSubjects(data);
        }).catch((failure) => { if (active) setError(message(failure)); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [sourceClass, subject, author, classId, subjectSlug, authorSlug]);

    useEffect(() => {
        if (!busy && !plan?.started) return undefined;
        const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [busy, plan]);

    async function copy() {
        if (running.current || !plan) return;
        running.current = true; setBusy(true); setError("");
        try {
            const result = await runModuleCopy(plan, setCompleted);
            onCopied(result);
        } catch (failure) {
            setError(message(failure));
        } finally {
            running.current = false; setBusy(false);
        }
    }

    const locked = busy || Boolean(plan?.started);
    return (
        <Dialog open fullWidth maxWidth="sm" onClose={busy ? undefined : onClose}>
            <DialogTitle>Скопіювати всі модулі</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Alert severity="info">
                        Будуть створені незалежні копії модулів із завданнями та секціями, включно з
                        вимкненими. Медіафайли залишаться спільними. Не закривайте сторінку до завершення.
                    </Alert>
                    <TextField select label="Клас-джерело" value={sourceClass} disabled={locked}
                        onChange={(event) => {
                            setSourceClass(event.target.value); setSubject(""); setAuthor("");
                            setSubjects([]); setAuthors([]); setPlan(null); setLoading(true);
                        }}>
                        {Array.from({ length: 11 }, (_, index) => String(index + 1)).map((value) => (
                            <MenuItem key={value} value={value}>{value} клас</MenuItem>
                        ))}
                    </TextField>
                    <TextField select label="Предмет-джерело" value={subject} disabled={locked || loading}
                        onChange={(event) => {
                            setSubject(event.target.value); setAuthor(""); setAuthors([]); setPlan(null); setLoading(true);
                        }}>
                        {subjects.map((item) => <MenuItem key={item.subjectSlug} value={item.subjectSlug}>
                            {item.title || item.name || item.subjectSlug}
                        </MenuItem>)}
                    </TextField>
                    <TextField select label="Автор / підручник-джерело" value={author}
                        disabled={locked || loading || !subject} onChange={(event) => {
                            setAuthor(event.target.value); setPlan(null); setLoading(true);
                        }}>
                        {authors.map((item) => <MenuItem key={item.authorSlug} value={item.authorSlug}>
                            {item.name || item.authorSlug}{item.textbookTitle ? ` — ${item.textbookTitle}` : ""}
                        </MenuItem>)}
                    </TextField>
                    {loading && <CircularProgress size={24} />}
                    {!loading && subject && !author && !authors.length && !error &&
                        <Alert severity="info">У цьому предметі немає інших авторів.</Alert>}
                    {plan && <Typography>
                        Модулів: {plan.modulesTotal}, секцій: {plan.sectionsTotal}.
                    </Typography>}
                    {plan?.started && <>
                        <LinearProgress variant="determinate" value={100 * completed / plan.items.length} />
                        <Typography>Скопійовано секцій: {Math.min(completed, plan.sectionsTotal)} / {plan.sectionsTotal};
                            модулів: {Math.max(0, completed - plan.sectionsTotal)} / {plan.modulesTotal}.</Typography>
                    </>}
                    {error && <Alert severity="error">{error}</Alert>}
                    {error && plan?.started && <Alert severity="warning">
                        Уже створені копії збережено. Натисніть «Продовжити» після усунення помилки.
                        Якщо закрити діалог, можливість продовжити цю операцію буде втрачено.
                    </Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button disabled={busy} onClick={onClose}>Закрити</Button>
                <Button variant="contained" disabled={busy || loading || !plan} onClick={copy}
                    startIcon={busy ? <CircularProgress size={16} /> : undefined}>
                    {busy ? "Копіювання…" : plan?.started ? "Продовжити" : "Скопіювати всі модулі"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
